import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import apiClient from '../utils/apiClient';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { XCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const COLORS = ['#E94B3C', '#FF6B5B', '#f59e0b', '#10b981', '#8b5cf6'];

export default function Dashboard() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSystemCheck, setShowSystemCheck] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [systemCheckResults, setSystemCheckResults] = useState(null);
  const [checkingSystem, setCheckingSystem] = useState(false);
  const [systemHealth, setSystemHealth] = useState({
    freshdesk_api: 'healthy',
    brain_crm_api: 'healthy',
    wati_api: 'not_configured',
    facebook_api: 'not_configured',
    database: 'healthy',
    ai_model: 'healthy',
    queue_processing: 'healthy'
  });

  useEffect(() => {
    fetchMetrics();
    fetchSystemHealth();
    fetchConfidenceBreakdown();
  }, []);

  const fetchMetrics = async () => {
    const toastId = toast.loading('Loading dashboard metrics...');
    try {
      const response = await apiClient.get('/api/v1/dashboard/metrics');
      setMetrics(response.data);
      toast.success('Dashboard loaded successfully', { id: toastId });
    } catch (error) {
      console.error('Error fetching metrics:', error);
      toast.error('Failed to load dashboard metrics', { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemHealth = async () => {
    try {
      const response = await apiClient.get('/api/v1/dashboard/system-health');
      setSystemHealth(response.data);
    } catch (error) {
      console.error('Error fetching system health:', error);
    }
  };

  // The pie gives three numbers and no way to ask what is in them. This names
  // the rows: which channel, which intent, and the exact stored values — the
  // last one being what separates a formula artifact (one number repeating)
  // from a genuinely uncertain week (a spread).
  const fetchConfidenceBreakdown = async () => {
    try {
      const response = await apiClient.get('/api/v1/dashboard/confidence-breakdown');
      setBreakdown(response.data);
    } catch (error) {
      console.error('Error fetching confidence breakdown:', error);
    }
  };

  const performSystemCheck = async () => {
    setCheckingSystem(true);
    setSystemCheckResults(null);
    
    const checks = [
      { name: 'Freshdesk API', endpoint: '/api/v1/settings/test-freshdesk', method: 'post', delay: 1000 },
      { name: 'Brain CRM API', endpoint: '/api/v1/settings/test-brain-crm', method: 'post', delay: 1500 },
      { name: 'Database Connection', endpoint: '/api/v1/dashboard/system-health', method: 'get', delay: 800 },
      { name: 'AI Model', endpoint: '/api/v1/dashboard/system-health', method: 'get', delay: 500 },
    ];
    
    const results = [];
    
    for (const check of checks) {
      await new Promise(resolve => setTimeout(resolve, check.delay));
      
      try {
        const response = await apiClient[check.method](check.endpoint);
        
        // For system-health endpoint, extract specific component status
        if (check.endpoint.includes('system-health')) {
          const healthData = response.data;
          let componentStatus = 'healthy';
          let componentMessage = 'Connection successful';
          
          if (check.name === 'Database Connection') {
            // Database is healthy if we can fetch the health data
            componentStatus = 'healthy';
            componentMessage = 'Database connection successful';
          } else if (check.name === 'AI Model') {
            componentStatus = healthData.ai_model === 'healthy' ? 'healthy' : 'error';
            componentMessage = componentStatus === 'healthy' ? 'AI Model operational' : 'AI Model error';
          }
          
          results.push({
            name: check.name,
            status: componentStatus,
            message: componentMessage,
            details: healthData
          });
        } else {
          // For test endpoints
          results.push({
            name: check.name,
            status: 'healthy',
            message: response.data?.message || 'Connection successful',
            details: response.data
          });
        }
      } catch (error) {
        results.push({
          name: check.name,
          status: 'error',
          message: error.response?.data?.detail || error.message || 'Connection failed',
          details: null
        });
      }
      
      setSystemCheckResults([...results]);
    }
    
    setCheckingSystem(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading dashboard...</div>
      </div>
    );
  }

  const confidenceData = metrics?.confidence_distribution
    ? [
        { name: 'High (≥85%)', value: metrics.confidence_distribution.high },
        { name: 'Medium (60-84%)', value: metrics.confidence_distribution.medium },
        { name: 'Low (<60%)', value: metrics.confidence_distribution.low },
      ]
    : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-sm text-gray-600">
          Overview of email automation system performance
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Emails (24h)"
          value={metrics?.total_emails?.['24h'] || 0}
          color="blue"
          onClick={() => window.location.href = '/tickets'}
          clickable
        />
        <MetricCard
          title="Emails (7d)"
          value={metrics?.total_emails?.['7d'] || 0}
          color="green"
          onClick={() => window.location.href = '/tickets'}
          clickable
        />
        <MetricCard
          title="WhatsApp (24h)"
          value={metrics?.total_whatsapp?.['24h'] || 0}
          color="green"
          onClick={() => window.location.href = '/queue'}
          clickable
        />
        <MetricCard
          title="WhatsApp (7d)"
          value={metrics?.total_whatsapp?.['7d'] || 0}
          color="green"
          onClick={() => window.location.href = '/queue'}
          clickable
        />
        <MetricCard
          title="Facebook (24h)"
          value={metrics?.total_facebook?.['24h'] || 0}
          color="indigo"
          onClick={() => window.location.href = '/queue'}
          clickable
        />
        <MetricCard
          title="Facebook (7d)"
          value={metrics?.total_facebook?.['7d'] || 0}
          color="indigo"
          onClick={() => window.location.href = '/queue'}
          clickable
        />
        <MetricCard
          title="Automation Rate"
          value={`${metrics?.automation_rate || 0}%`}
          color="purple"
        />
        <MetricCard
          title="Current Queue"
          value={metrics?.queue_size || 0}
          color="orange"
          onClick={() => window.location.href = '/queue'}
          clickable
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Intent Distribution */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Intent Distribution (7 days)
          </h3>
          {metrics?.intent_distribution?.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={metrics.intent_distribution}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="intent" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#E94B3C" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No data available
            </div>
          )}
        </div>

        {/* Confidence Distribution */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Confidence Distribution
          </h3>
          {confidenceData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={confidenceData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {confidenceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No data available
            </div>
          )}

          {/* What is actually in those slices. Email stores a BLENDED score
              (classifier certainty plus how much of the customer's detail we
              hold), WhatsApp and Facebook store the raw classifier certainty —
              so a low email row can simply mean "no phone number yet". */}
          {breakdown && (
            <div className="mt-4 border-t border-gray-100 pt-3">
              <button
                onClick={() => setShowBreakdown(!showBreakdown)}
                className="text-sm text-gray-600 hover:text-gray-900"
              >
                {showBreakdown ? 'Hide' : "What's in these slices?"}
              </button>
              {showBreakdown && (
                <div className="mt-3 space-y-4 text-sm">
                  <p className="text-xs text-gray-500">
                    Email stores a blended score (how certain the read was, plus
                    how much of the customer's detail we hold). WhatsApp and
                    Facebook store the read's certainty alone. A low email row
                    can mean we simply don't have their phone or vehicle yet.
                  </p>

                  <div>
                    <div className="font-medium text-gray-900 mb-1">By channel</div>
                    <table className="w-full text-xs">
                      <tbody>
                        {breakdown.by_channel.map((r, i) => (
                          <tr key={i} className="border-b border-gray-50">
                            <td className="py-1 text-gray-700">{r.channel}</td>
                            <td className="py-1 text-gray-500">{r.bucket}</td>
                            <td className="py-1 text-right text-gray-900">{r.rows}</td>
                            <td className="py-1 text-right text-gray-500">
                              {r.tickets} thread{r.tickets === 1 ? '' : 's'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div>
                    <div className="font-medium text-gray-900 mb-1">
                      Exact scores, commonest first
                    </div>
                    <table className="w-full text-xs">
                      <tbody>
                        {breakdown.values.slice(0, 12).map((v, i) => (
                          <tr key={i} className="border-b border-gray-50">
                            <td className="py-1 text-gray-900">
                              {v.confidence === null ? '—' : `${v.confidence}%`}
                            </td>
                            <td className="py-1 text-gray-500">{v.channel}</td>
                            <td className="py-1 text-gray-500">{v.bucket}</td>
                            <td className="py-1 text-right text-gray-900">{v.rows}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div>
                    <div className="font-medium text-gray-900 mb-1">
                      Lowest-scoring intents
                    </div>
                    <table className="w-full text-xs">
                      <tbody>
                        {breakdown.by_intent
                          .filter((r) => r.bucket === 'low')
                          .slice(0, 8)
                          .map((r, i) => (
                            <tr key={i} className="border-b border-gray-50">
                              <td className="py-1 text-gray-700">{r.intent}</td>
                              <td className="py-1 text-right text-gray-900">{r.rows}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>

                  <p className="text-xs text-gray-400">
                    Counts are classification rows. A thread is scored again on
                    every pass, so one thread can appear in two slices — the
                    percentages above are not shares of threads.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* System Health */}
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">System Health</h3>
          <button
            onClick={() => {
              setShowSystemCheck(true);
              performSystemCheck();
            }}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            System Check
          </button>
        </div>
        
        {/* Color Key */}
        <div className="flex items-center space-x-6 mb-4 pb-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="text-xs text-gray-600">Healthy</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <span className="text-xs text-gray-600">Warning</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span className="text-xs text-gray-600">Error</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-gray-400"></div>
            <span className="text-xs text-gray-600">Not Configured</span>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <HealthIndicator label="Freshdesk API" status={systemHealth.freshdesk_api} />
          <HealthIndicator label="Brain CRM API" status={systemHealth.brain_crm_api} />
          <HealthIndicator label="Wati API (WhatsApp)" status={systemHealth.wati_api} />
          <HealthIndicator label="Facebook" status={systemHealth.facebook_api} />
          <HealthIndicator label="Database" status={systemHealth.database} />
          <HealthIndicator label="AI Model" status={systemHealth.ai_model} />
          <HealthIndicator label="Queue Processing" status={systemHealth.queue_processing} />
        </div>
      </div>
      
      {/* System Check Modal */}
      <SystemCheckModal
        isOpen={showSystemCheck}
        onClose={() => setShowSystemCheck(false)}
        results={systemCheckResults}
        checking={checkingSystem}
      />
    </div>
  );
}

function MetricCard({ title, value, color, onClick, clickable }) {
  const colorClasses = {
    blue: 'bg-red-50 text-michanic',
    green: 'bg-green-50 text-green-700',
    purple: 'bg-purple-50 text-purple-700',
    orange: 'bg-orange-50 text-orange-700',
    indigo: 'bg-indigo-50 text-indigo-700',
  };

  return (
    <div 
      className={`bg-white overflow-hidden shadow rounded-lg ${
        clickable ? 'cursor-pointer hover:shadow-lg transition-shadow duration-200' : ''
      }`}
      onClick={clickable ? onClick : undefined}
    >
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-1">
            <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
            <dd className="mt-1 text-dashboard-heading font-semibold text-gray-900">{value}</dd>
          </div>
          <div className={`flex-shrink-0 p-dashboard-icon rounded-full ${colorClasses[color]}`}>
            <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

function HealthIndicator({ label, status }) {
  const statusColors = {
    healthy: 'bg-green-500',
    warning: 'bg-yellow-500',
    error: 'bg-red-500',
    disabled: 'bg-yellow-500',
    not_configured: 'bg-gray-400',
  };

  const statusLabels = {
    healthy: '',
    warning: ' (Warning)',
    error: ' (Error)',
    disabled: ' (Disabled)',
    not_configured: ' (Not Configured)',
  };

  return (
    <div className="flex items-center space-x-2">
      <div className={`w-3 h-3 rounded-full ${statusColors[status] || 'bg-gray-400'}`}></div>
      <span className="text-sm text-gray-700">{label}{statusLabels[status] || ''}</span>
    </div>
  );
}

function SystemCheckModal({ isOpen, onClose, results, checking }) {
  if (!isOpen) return null;

  const allChecksComplete = results && results.length === 4;
  const hasErrors = results?.some(r => r.status === 'error');

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">System Health Check</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <XCircleIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Progress Indicator */}
          {checking && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Running diagnostics...
                </span>
                <span className="text-sm text-gray-500">
                  {results?.length || 0} / 4 checks complete
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${((results?.length || 0) / 4) * 100}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Results Summary */}
          {allChecksComplete && (
            <div className={`mb-6 p-4 rounded-lg ${hasErrors ? 'bg-red-50' : 'bg-green-50'}`}>
              <div className="flex items-center">
                {hasErrors ? (
                  <XCircleIcon className="h-6 w-6 text-red-600 mr-3" />
                ) : (
                  <CheckCircleIcon className="h-6 w-6 text-green-600 mr-3" />
                )}
                <div>
                  <h3 className={`font-semibold ${hasErrors ? 'text-red-800' : 'text-green-800'}`}>
                    {hasErrors ? 'Issues Detected' : 'All Systems Operational'}
                  </h3>
                  <p className={`text-sm ${hasErrors ? 'text-red-700' : 'text-green-700'}`}>
                    {hasErrors
                      ? 'Some system components require attention'
                      : 'All system components are functioning normally'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Check Results */}
          <div className="space-y-4">
            {[
              'Freshdesk API',
              'Brain CRM API',
              'Database Connection',
              'Queue Processing'
            ].map((checkName, index) => {
              const result = results?.find(r => r.name === checkName);
              const isChecking = checking && (!result);
              const isComplete = !!result;

              return (
                <div
                  key={checkName}
                  className="border border-gray-200 rounded-lg p-4 transition-all duration-300"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1">
                      {/* Status Indicator */}
                      {isChecking && (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                      )}
                      {isComplete && result.status === 'healthy' && (
                        <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      )}
                      {isComplete && result.status === 'error' && (
                        <XCircleIcon className="h-5 w-5 text-red-500" />
                      )}
                      {!isChecking && !isComplete && (
                        <div className="h-5 w-5 rounded-full border-2 border-gray-300"></div>
                      )}

                      {/* Check Name */}
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{checkName}</h4>
                        {isChecking && (
                          <p className="text-sm text-gray-500">Checking...</p>
                        )}
                        {isComplete && (
                          <p className={`text-sm ${result.status === 'healthy' ? 'text-green-600' : 'text-red-600'}`}>
                            {result.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Status Badge */}
                    {isComplete && (
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          result.status === 'healthy'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {result.status === 'healthy' ? 'Healthy' : 'Error'}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
